import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Card module
 */
export default class SocialHighlights extends MessageBus {
  /**
   * Constructor function of the CardFunc class. Must receive one arguments, a DOM
   * node representing a delivery ticket.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} CardFunc A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} CardFunc A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.wrapper = this.element.querySelector(
      `.${CLASS_NAMES.SOCIAL_HIGHLIGHTS_POSTS}`
    );
    this.posts = this.wrapper.querySelectorAll(
      `.${CLASS_NAMES.SOCIAL_HIGHLIGHTS_POST}`
    );
    this.gridSizer = this.wrapper.querySelector(
      `.${CLASS_NAMES.SOCIAL_HIGHLIGHTS_GRID_SIZER}`
    );
    this.gutterSizer = this.wrapper.querySelector(
      `.${CLASS_NAMES.SOCIAL_HIGHLIGHTS_GUTTER_SIZER}`
    );
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} Search A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.calculateLayout();

    // Update layout on DOM changes
    this.observer = new MutationObserver(() => {
      this.calculateLayout();
    });
    this.observer.observe(this.wrapper, { childList: true, subtree: true });

    // Update layout on window resize
    window.addEventListener(EVENTS.RESIZE, this.resizeHandler);
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.calculateLayout = this.calculateLayout.bind(this);
    this.resizeHandler = () => {
      this.calculateLayout();
      setTimeout(() => this.calculateLayout(), 1000);
      setTimeout(() => this.calculateLayout(), 3000);
    };
    return this;
  }

  calculateLayout() {
    const containerWidth = this.wrapper.offsetWidth;
    const columnWidth = this.gridSizer.offsetWidth;
    const gutterWidth = this.gutterSizer.offsetWidth;

    // Calculate number of columns that fit in container
    const numColumns = Math.floor(
      (containerWidth + gutterWidth) / (columnWidth + gutterWidth)
    );
    const columns = Array(numColumns).fill(0); // Track column heights

    // Position each post
    this.posts.forEach((post) => {
      // Find shortest column
      const minHeight = Math.min(...columns);
      const shortestColumn = columns.indexOf(minHeight);

      // Calculate x and y position
      const xPos = shortestColumn * (columnWidth + gutterWidth);
      const yPos = minHeight;

      // Position the post using a helper method
      this.applyPostStyles(post, {
        transform: `translate(${xPos}px, ${yPos}px)`,
        position: 'absolute',
        width: `${columnWidth}px`
      });

      // Update column height
      columns[shortestColumn] += post.offsetHeight + gutterWidth;
    });

    // Set container height to tallest column
    this.wrapper.style.height = `${Math.max(...columns)}px`;
    this.wrapper.style.position = 'relative';
  }

  /**
   * Apply styles to a post element using style.setProperty
   * @param {HTMLElement} post - The post element to style
   * @param {object} styles - Object containing styles to apply
   * @returns {object} A reference to the current instance of the class
   * @private
   */
  applyPostStyles(post, styles) {
    Object.entries(styles).forEach(([property, value]) => {
      post.style.setProperty(property, value);
    });
    return this;
  }
}
